// frontend/src/pages/RestDayMonitoring/index.js
import React, { useState } from 'react';
import {
  Users,
  AlertTriangle,
  CalendarDays,
  MailCheck,
  Calendar,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTabPersistence } from '../../hooks/usePersistence';
import useRestDayData from './hooks/useRestDayData';
import RestDayStatsCards from './components/RestDayStatsCards';
import EmployeeRestDayTable from './components/EmployeeRestDayTable';
import EmployeeRestDayDetailModal from './components/EmployeeRestDayDetailModal';
import RestDayAlerts from './components/RestDayAlerts';
import RestDayNotificationCoverage from './components/RestDayNotificationCoverage';
import { CountBadge } from '../../components/UI/CountBadge';
import PillSelect from '../../components/UI/PillSelect';

/**
 * RestDayMonitoring - 12-Day Rest Day Interval Compliance Monitoring Page
 * 
 * Admin page for HR to monitor and manage MOM Employment Act s36 compliance.
 * Ensures no employee works more than 12 consecutive days without a rest day.
 * 
 * Features:
 * - Dashboard with key compliance metrics
 * - Employee tracking with streak progress bars
 * - Active alerts with acknowledge workflow
 * - Design: admin red theme per design-system-reference.md
 * 
 * Mirrors OvertimeMonitoring page structure for consistency.
 */
const RestDayMonitoring = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useTabPersistence('rest-day-monitoring', 'employees', ['employees', 'alerts', 'coverage']);
  
  const {
    stats,
    employees,
    alerts,
    loading,
    error,
    filters,
    refetch,
    updateFilters,
    acknowledgeAlert,
    getEmployeeDetail,
    getNotificationCoverage,
    selectedMonth,
    monthOptions,
    changeMonth,
  } = useRestDayData();

  // Modal state for employee detail view
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Tab configuration - 2 tabs: Employee Tracking (with stats), Alerts
  const tabs = [
    { 
      id: 'employees', 
      name: 'Employee Tracking', 
      icon: Users,
      count: employees?.filter(e => e.complianceStatus !== 'compliant').length || 0,
    },
    { 
      id: 'alerts', 
      name: 'Alerts', 
      icon: AlertTriangle,
      count: alerts?.filter(a => a.alertStatus === 'active').length || 0,
    },
    { 
      id: 'coverage', 
      name: 'Notification Coverage',
      icon: MailCheck,
    },
  ];

  // Handle alert acknowledgment
  const handleAcknowledgeAlert = async (alertId) => {
    await acknowledgeAlert(alertId, user?.staff_id);
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'employees':
        return (
          <div className="space-y-6">
            {/* Stats Cards at top — click filters the table */}
            <RestDayStatsCards 
              stats={stats} 
              loading={loading}
              onCardClick={(value) => updateFilters({ status: value })}
              activeFilter={filters.status}
            />
            
            {/* Employee Table */}
            <EmployeeRestDayTable
              employees={employees}
              loading={loading}
              filters={filters}
              onFilterChange={updateFilters}
              onViewEmployee={(emp) => setSelectedEmployee(emp)}
            />
          </div>
        );

      case 'alerts':
        return (
          <RestDayAlerts
            alerts={alerts}
            loading={loading}
            onAcknowledge={handleAcknowledgeAlert}
            currentUser={user}
          />
        );

      case 'coverage':
        return (
          <RestDayNotificationCoverage
            getNotificationCoverage={getNotificationCoverage}
            selectedMonth={selectedMonth}
            onChangeMonth={changeMonth}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-1 min-w-0">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
          </div>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Rest Day Interval Monitoring</h1>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">
          MOM Employment Act s36 • Maximum 12 consecutive working days
        </p>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <p className="text-sm text-red-800">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            {error}
          </p>
        </div>
      )}

      {/* Tab Navigation - Admin theme with red. Month selector sits inline
          with tabs (same layout as Overtime Monitoring) — kept outside the
          horizontally-scrolling strip so PillSelect dropdowns aren't clipped. */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center mb-4 md:mb-6">
        <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth flex-1 min-w-0">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`tab-btn-admin ${isActive ? 'active' : ''}`}
              >
                <Icon className="w-4 h-4" />
                {tab.name}
                {tab.count > 0 && (
                  <CountBadge count={tab.count} theme="admin" active={isActive} className="ml-1" />
                )}
              </button>
            );
          })}
        </div>

        <div className="w-full md:w-auto md:flex-shrink-0">
          <PillSelect
            options={monthOptions}
            value={selectedMonth || ''}
            onChange={(val) => changeMonth?.(val)}
            theme="admin"
            icon={Calendar}
            iconColor="text-red-600"
            className="w-full md:w-auto"
          />
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {renderTabContent()}
      </div>

      {/* Footer Info */}
      <div className="text-xs text-gray-500 text-center pt-4 border-t border-gray-200">
        Data source: ClockEvent + AttendanceRecord + Leave •
        Computed live as of {stats?.asOfDate
          ? new Date(stats.asOfDate + 'T00:00:00').toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' })
          : '—'}
      </div>

      {/* Employee Detail Modal */}
      <EmployeeRestDayDetailModal
        isOpen={!!selectedEmployee}
        onClose={() => setSelectedEmployee(null)}
        employee={selectedEmployee}
        getEmployeeDetail={getEmployeeDetail}
      />
    </div>
  );
};

export default RestDayMonitoring;
